import React, { useRef } from 'react';
import styles from './Banner.module.scss';
import Button from '../Button/Button';
import clsx from 'clsx';
import { TELEGRAM_CTA_URL } from '../../constants/cta';
import { HERO_CONTENT } from '../../constants/marketingContent';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { trackGoal } from '../../analytics/metrika';

gsap.registerPlugin(useGSAP);

const badgeIcons = [
  <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d="M0 0h24v24H0z" fill="none" />
    <g fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2">
      <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09" />
      <path d="M9 12a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.4 22.4 0 0 1-4 2z" />
      <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 .05 5 .05" />
    </g>
  </svg>,

  <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d="M0 0h24v24H0z" fill="none" />
    <g fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2">
      <path d="M8 2v4m8-4v4" />
      <rect width="18" height="18" x="3" y="4" rx="2" />
      <path d="M3 10h18" />
    </g>
  </svg>,
  <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24">
    <path d="M0 0h24v24H0z" fill="none" />
    <g fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2">
      <path d="M2.586 17.414A2 2 0 0 0 2 18.828V21a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h1a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h.172a2 2 0 0 0 1.414-.586l.814-.814a6.5 6.5 0 1 0-4-4z" />
      <circle cx="16.5" cy="7.5" r=".5" fill="currentColor" />
    </g>
  </svg>,
];

const Banner = () => {
  const bannerRef = useRef(null);
  const titleRef = useRef(null);
  const imageRef = useRef(null);
  const textRef = useRef(null);
  const actionsRef = useRef(null);
  const secondButtonRef = useRef(null);
  const metaBadgesRef = useRef([]);

  useGSAP(
    () => {
      const title = titleRef.current;
      const bannerImage = imageRef.current;
      const text = textRef.current;
      const primaryButton = actionsRef.current?.querySelector('button');
      const secondButton = secondButtonRef.current;
      const metaBadges = metaBadgesRef.current.filter(Boolean);

      const fadeTargets = [title, bannerImage, text, primaryButton, secondButton, ...metaBadges].filter(Boolean);
      const moveTargets = [title, text, primaryButton, secondButton, ...metaBadges].filter(Boolean);
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (!fadeTargets.length) {
        return;
      }

      gsap.set(fadeTargets, {
        autoAlpha: 0,
      });
      gsap.set(moveTargets, { y: 24 });

      gsap
        .timeline({ delay: 0.4, defaults: { ease: 'power3.out' } })
        .to(title, { autoAlpha: 1, y: 0, duration: 0.65 })
        .to(bannerImage, { autoAlpha: 1, duration: 0.65 }, '<')
        .to(text, { autoAlpha: 1, y: 0, duration: 0.5 }, '-=0.18')
        .to(primaryButton, { autoAlpha: 1, y: 0, duration: 0.45 }, '-=0.1')
        .to(secondButton, { autoAlpha: 1, y: 0, duration: 0.45 }, '-=0.1')
        .to(metaBadges, { autoAlpha: 1, y: 0, duration: 0.4, stagger: 0.14 }, '-=0.08')
        .add(() => {
          if (prefersReducedMotion || !metaBadges.length) {
            return;
          }

          const isMobile = window.matchMedia('(max-width: 640px)').matches;
          const xRange = isMobile ? 2 : 4;
          const yRange = isMobile ? 2.6 : 5.5;
          const rotationRange = isMobile ? 0.9 : 1.6;

          metaBadges.forEach((badge) => {
            gsap.to(badge, {
              x: () => gsap.utils.random(-xRange, xRange, 0.1),
              y: () => gsap.utils.random(-yRange, yRange, 0.1),
              rotation: () => gsap.utils.random(-rotationRange, rotationRange, 0.1),
              duration: () => gsap.utils.random(2.6, 4.8, 0.1),
              ease: 'sine.inOut',
              repeat: -1,
              yoyo: true,
              repeatRefresh: true,
              delay: gsap.utils.random(0, 0.9, 0.05),
            });
          });
        });
    },
    { scope: bannerRef },
  );

  const openTelegram = () => {
    trackGoal('cta_telegram_click', { source: 'hero' });
    window.open(TELEGRAM_CTA_URL, '_blank');
  };

  const scrollToCases = () => {
    const worksElement = document.getElementById('works');
    if (!worksElement) {
      return;
    }
    const elementPosition = worksElement.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - 70;
    window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
  };

  return (
    <div className={clsx(styles.wrap)} ref={bannerRef}>
      <div className="container">
        <div className={clsx(styles.inner)}>
          <div className={clsx(styles.left)}>
            <h1 className={clsx(styles.title)} ref={titleRef}>
              {HERO_CONTENT.title}
            </h1>
            <div className={clsx(styles.leftBody)}>
              <p className={clsx(styles.text)} ref={textRef}>
                {HERO_CONTENT.description.map((line, index) => (
                  <React.Fragment key={line}>
                    {line}
                    {index < HERO_CONTENT.description.length - 1 ? <br /> : null}
                  </React.Fragment>
                ))}
              </p>
              <div className={clsx(styles.actions)} ref={actionsRef}>
                <Button
                  iconPosition="right"
                  icon={
                    <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                      <path d="M0 0h24v24H0z" fill="none" />
                      <g fill="none" fillRule="evenodd">
                        <path d="m12.593 23.258l-.011.002l-.071.035l-.02.004l-.014-.004l-.071-.035q-.016-.005-.024.005l-.004.01l-.017.428l.005.02l.01.013l.104.074l.015.004l.012-.004l.104-.074l.012-.016l.004-.017l-.017-.427q-.004-.016-.017-.018m.265-.113l-.013.002l-.185.093l-.01.01l-.003.011l.018.43l.005.012l.008.007l.201.093q.019.005.029-.008l.004-.014l-.034-.614q-.005-.018-.02-.022m-.715.002a.02.02 0 0 0-.027.006l-.006.014l-.034.614q.001.018.017.024l.015-.002l.201-.093l.01-.008l.004-.011l.017-.43l-.003-.012l-.01-.01z" />
                        <path
                          fill="currentColor"
                          d="M19.777 4.43a1.5 1.5 0 0 1 2.062 1.626l-2.268 13.757c-.22 1.327-1.676 2.088-2.893 1.427c-1.018-.553-2.53-1.405-3.89-2.294c-.68-.445-2.763-1.87-2.507-2.884c.22-.867 3.72-4.125 5.72-6.062c.785-.761.427-1.2-.5-.5c-2.302 1.738-5.998 4.381-7.22 5.125c-1.078.656-1.64.768-2.312.656c-1.226-.204-2.363-.52-3.291-.905c-1.254-.52-1.193-2.244-.001-2.746z"
                        />
                      </g>
                    </svg>
                  }
                  onClick={openTelegram}
                  data-metrika-label={HERO_CONTENT.primaryCta}
                  data-metrika-section="hero">
                  {HERO_CONTENT.primaryCta}
                </Button>
                <button
                  className={clsx(styles.secondaryButton)}
                  onClick={scrollToCases}
                  ref={secondButtonRef}
                  data-metrika-label={HERO_CONTENT.secondaryCta}
                  data-metrika-section="hero">
                  {HERO_CONTENT.secondaryCta}
                  <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                    <path d="M0 0h24v24H0z" fill="none" />
                    <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 5v14m7-7l-7 7l-7-7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
          <div className={clsx(styles.right)}>
            <img src="/img-webp/banner-4.webp" alt="" className={clsx(styles.image)} ref={imageRef} />
            <div className={clsx(styles.metaCloud)}>
              {HERO_CONTENT.badges.map((badge, index) => (
                <span
                  key={badge}
                  className={clsx(styles.metaBadge, styles[`metaBadge${index + 1}`])}
                  ref={(el) => {
                    metaBadgesRef.current[index] = el;
                  }}>
                  {badgeIcons[index] ? <span className={styles.metaBadgeIcon}>{badgeIcons[index]}</span> : null}
                  <span>{badge}</span>
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Banner;
